import { useEffect, useState } from 'react';

import { supabase } from '../lib/supabase';

// Histórico de pagamentos do usuário: assinatura (asaas_payments) + perfis avulsos
// (artist_purchases), unificados e ordenados por data. RLS já restringe ao próprio usuário.
//
// A busca vive aqui, separada da tela, porque a página usa a mesma lista duas vezes: no resumo
// do topo (total, quantidade, último pago) e na tabela.
//
// Subiu de `src/pages/Payments` para o núcleo quando o app nativo ganhou a mesma tela. As duas
// leem as MESMAS duas tabelas e normalizam os status do mesmo jeito — dois normalizadores dariam
// dois "Pendente" com significados diferentes para a mesma linha do banco.

export interface PayItem {
  id: string;
  kind: 'subscription' | 'profile';
  title: string;
  amount: number;
  date: string | null;
  billing: string | null;
  status: PayStatus;
}

export type PayStatus = 'paid' | 'pending' | 'failed' | 'overdue' | 'canceled' | 'refunded';

export const STATUS_META: Record<PayStatus, { label: string; tone: 'ok' | 'warn' | 'danger' | 'mute' }> = {
  paid: { label: 'Pago', tone: 'ok' },
  pending: { label: 'Pendente', tone: 'warn' },
  failed: { label: 'Falhou', tone: 'danger' },
  overdue: { label: 'Vencido', tone: 'danger' },
  canceled: { label: 'Cancelado', tone: 'mute' },
  refunded: { label: 'Estornado', tone: 'mute' },
};

// Normaliza os status das duas tabelas num conjunto comum.
const normStatus = (s: string | null): PayStatus => {
  switch ((s || '').toLowerCase()) {
    case 'received':
    case 'confirmed':
      return 'paid';
    case 'failed':
      return 'failed';
    case 'overdue':
      return 'overdue';
    case 'deleted':
      return 'canceled';
    case 'refunded':
      return 'refunded';
    default:
      return 'pending';
  }
};

export const billingLabel = (b: string | null): string | null => {
  switch ((b || '').toUpperCase()) {
    case 'PIX':
      return 'PIX';
    case 'CREDIT_CARD':
      return 'Cartão de crédito';
    case 'DEBIT_CARD':
      return 'Cartão de débito';
    default:
      return b || null;
  }
};

export const fmtBRL = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
export const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '—');

/**
 * O id do usuário vem de FORA, e não do store.
 *
 * O hook lia `state.auth.user` — e o app nativo não alimenta essa fatia (ele guarda a sessão
 * direto do supabase-js). O resultado era a tela ficar girando para sempre: sem usuário, o
 * efeito saía antes de desligar o `loading`, e nada acontecia nunca mais.
 */
export function usePaymentHistory(userId?: string | null) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<PayItem[]>([]);
  // A leitura que falha não é "nenhum pagamento" (QA do pagamento, 04/10/2026): a tela dizia que a
  // pessoa nunca pagou nada quando só a consulta tinha caído.
  const [falhou, setFalhou] = useState(false);
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    // Sem usuário não há o que buscar — mas há o que dizer: uma lista vazia, e não um giro
    // eterno.
    if (!userId) { setItems([]); setLoading(false); return undefined; }
    let active = true;
    setLoading(true);
    setFalhou(false);
    (async () => {
      try {
        const [subs, profiles] = await Promise.all([
          supabase.from('asaas_payments').select('id, value, status, payment_date, billing_type, created_at').eq('user_id', userId),
          supabase.from('artist_purchases').select('id, amount, status, billing_type, paid_at, created_at, artist_name').eq('user_id', userId),
        ]);
        if (!active) return;
        if (subs.error || profiles.error) { setFalhou(true); setItems([]); return; }
        const subItems: PayItem[] = (subs.data || []).map((p: any) => ({
          id: `s_${p.id}`,
          kind: 'subscription',
          title: 'Assinatura Maestra PRO',
          amount: Number(p.value) || 0,
          date: p.payment_date || p.created_at || null,
          billing: p.billing_type,
          status: normStatus(p.status),
        }));
        const profItems: PayItem[] = (profiles.data || []).map((p: any) => ({
          id: `p_${p.id}`,
          kind: 'profile',
          title: p.artist_name ? `Perfil de ${p.artist_name}` : 'Perfil de artista',
          amount: Number(p.amount) || 0,
          date: p.paid_at || p.created_at || null,
          billing: p.billing_type,
          status: normStatus(p.status),
        }));
        setItems([...subItems, ...profItems].sort(
          (a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime()
        ));
      } catch {
        if (active) { setItems([]); setFalhou(true); }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [userId, tentativa]);

  return { items, loading, falhou, tentarDeNovo: () => setTentativa((t) => t + 1) };
}
